import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import ImmersiveLayout from '../components/ImmersiveLayout';
import {
  CheckCircle, Loader2, CreditCard, Copy, Gift, QrCode, Plus, MapPin, MessageCircle,
  ArrowRight, Lock, Mail, Phone, User, Calendar, FileText, Ticket, Coins
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

export default function CheckoutPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { cart, fetchCart } = useCart();
  const navigate = useNavigate();
  const id = language === 'id';

  const [bankConfig, setBankConfig] = useState(null);
  const [addonPrices, setAddonPrices] = useState(null);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [addMemoryPage, setAddMemoryPage] = useState(false);
  const [addGiftCard, setAddGiftCard] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState('');
  const [loyaltyBalance, setLoyaltyBalance] = useState(0);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [form, setForm] = useState({
    delivery_name: '', delivery_phone: '', delivery_address: '',
    delivery_date: '', delivery_notes: ''
  });

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    axios.get(`${API}/settings/bank-transfer`).then(r => setBankConfig(r.data)).catch(() => {});
    axios.get(`${API}/settings/addon-prices`).then(r => setAddonPrices(r.data)).catch(() => {});
    axios.get(`${API}/delivery-zones`).then(r => {
      setDeliveryZones(r.data.zones || []);
      if (r.data.zones?.length > 0) setSelectedZone(r.data.zones[0]);
    }).catch(() => {});
    // Fetch loyalty balance
    axios.get(`${API}/user/loyalty`, { withCredentials: true })
      .then(r => setLoyaltyBalance(r.data.points || 0))
      .catch(() => {});
  }, [user, navigate]);

  const fmt = (p) => new Intl.NumberFormat('id-ID').format(p);

  const subtotal = cart?.items?.reduce((sum, item) => {
    const price = item.product?.price || 0;
    return sum + price * (item.quantity || 1);
  }, 0) || 0;

  const deliveryFee = selectedZone?.fee || 25000;
  const memoryPagePrice = addonPrices?.memory_page || 75000;
  const giftCardPrice = addonPrices?.gift_card || 35000;
  const promoDiscount = promoResult?.discount_amount || 0;
  const loyaltyDiscount = redeemPoints;
  const total = subtotal + deliveryFee + (addMemoryPage ? memoryPagePrice : 0) + (addGiftCard ? giftCardPrice : 0) - promoDiscount - loyaltyDiscount;

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promo-codes/validate`, { code: promoCode, subtotal });
      setPromoResult(res.data);
      toast.success(id ? `Diskon Rp ${fmt(res.data.discount_amount)} diterapkan!` : `Discount Rp ${fmt(res.data.discount_amount)} applied!`);
    } catch (err) {
      const msg = err.response?.data?.detail || (id ? 'Kode tidak valid' : 'Invalid code');
      toast.error(msg);
      setPromoResult(null);
    }
    setPromoLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.delivery_name || !form.delivery_phone || !form.delivery_address) {
      toast.error(id ? 'Mohon lengkapi semua field' : 'Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/orders`, {
        ...form, payment_method: 'bank_transfer', language,
        promo_code: promoResult?.code || null,
        delivery_zone_id: selectedZone?.zone_id || null,
        redeem_points: redeemPoints
      }, { withCredentials: true });
      setOrderResult(res.data);
      fetchCart();
      try {
        const waRes = await axios.get(`${API}/orders/${res.data.order_id}/whatsapp`, { withCredentials: true });
        setWhatsappLink(waRes.data.whatsapp_link);
      } catch {}
    } catch (err) {
      toast.error(id ? 'Terjadi kesalahan' : 'Something went wrong');
    }
    setLoading(false);
  };

  // -------- Success state --------
  if (orderResult) {
    return (
      <ImmersiveLayout>
        <section className="min-h-[100vh] flex items-center justify-center px-6 py-24" data-testid="order-success">
          <div className="max-w-lg w-full text-center">
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="w-24 h-24 mx-auto mb-8 rounded-full bg-seikat-lime/15 flex items-center justify-center"
            >
              <CheckCircle size={40} className="text-seikat-lime" strokeWidth={1.5} />
            </motion.div>
            <p className="eyebrow-immersive">{id ? 'Pesanan tercipta' : 'Order placed'}</p>
            <h1 className="mt-3 font-heading text-5xl text-seikat-bone">
              {id ? <>Terima kasih, <em>kekasih bunga</em>.</> : <>Thank you, <em>flower friend</em>.</>}
            </h1>
            <p className="mt-4 font-body text-seikat-bone/65">
              {id ? 'Kami akan merangkai dengan penuh hati.' : 'We will weave it with all our heart.'}
            </p>
            <div className="mt-6 inline-flex items-center gap-3 glass-dark rounded-full px-5 py-2">
              <span className="text-xs tracking-widest uppercase text-seikat-bone-muted">Order ID</span>
              <code className="text-seikat-pink font-medium" data-testid="order-id">{orderResult.order_id}</code>
            </div>

            {bankConfig && (
              <div className="mt-10 glass-dark rounded-2xl p-6 text-left">
                <h3 className="font-heading text-2xl text-seikat-bone flex items-center gap-2">
                  <CreditCard size={16} className="text-seikat-pink" /> {id ? 'Instruksi Transfer' : 'Bank Transfer Instructions'}
                </h3>
                <div className="mt-5 space-y-2.5 text-sm font-body">
                  <Row label="Bank" value={bankConfig.bank_name} />
                  <Row
                    label={id ? 'No. Rekening' : 'Account No.'}
                    value={
                      <button onClick={() => { navigator.clipboard.writeText(bankConfig.account_number); toast.success('Copied'); }} className="inline-flex items-center gap-1 text-seikat-bone hover:text-seikat-pink transition-colors">
                        {bankConfig.account_number} <Copy size={12} />
                      </button>
                    }
                  />
                  <Row label={id ? 'Atas Nama' : 'Account Name'} value={bankConfig.account_name} />
                  <Row label="Total" value={<span className="text-seikat-pink font-medium">Rp {fmt(orderResult.total)}</span>} />
                </div>
                <p className="mt-5 text-xs text-seikat-bone/55 leading-relaxed">
                  {id ? bankConfig.instructions_id : bankConfig.instructions_en}
                </p>
              </div>
            )}

            <div className="mt-10 flex flex-col gap-3">
              {whatsappLink && (
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                  <button className="btn-pill btn-pill--solid w-full justify-center" data-testid="whatsapp-confirm-btn" style={{ background: '#25D366', color: '#FFF', borderColor: '#25D366' }}>
                    <MessageCircle size={14} /> {id ? 'Konfirmasi via WhatsApp' : 'Confirm via WhatsApp'}
                  </button>
                </a>
              )}
              <div className="flex gap-3">
                <button onClick={() => navigate('/orders')} className="btn-pill flex-1 justify-center" data-testid="view-orders-btn" data-magnet>
                  {id ? 'Lihat Pesanan' : 'View Orders'} <ArrowRight size={14} />
                </button>
                <button onClick={() => navigate('/')} className="btn-pill flex-1 justify-center" data-testid="back-home-btn" data-magnet>
                  {id ? 'Kembali' : 'Home'}
                </button>
              </div>
            </div>
          </div>
        </section>
      </ImmersiveLayout>
    );
  }

  // -------- Empty cart guard --------
  if (!cart?.items?.length) {
    return (
      <ImmersiveLayout>
        <section className="min-h-[80vh] flex items-center justify-center px-6 py-24" data-testid="checkout-empty">
          <div className="text-center max-w-md">
            <p className="eyebrow-immersive">{id ? 'Checkout' : 'Checkout'}</p>
            <h1 className="mt-3 font-heading text-4xl text-seikat-bone">
              {id ? <>Keranjang masih <em>sunyi</em>.</> : <>Your cart is still <em>silent</em>.</>}
            </h1>
            <p className="mt-4 font-body text-seikat-bone/60 text-sm">
              {id ? 'Pilih rangkaian dulu agar kami bisa merangkainya untukmu.' : 'Pick an arrangement so we can weave it for you.'}
            </p>
            <Link to="/catalog">
              <button className="btn-pill btn-pill--solid mt-8" data-testid="checkout-empty-browse">
                {id ? 'Jelajahi Bouquet' : 'Browse Bouquets'} <ArrowRight size={14} />
              </button>
            </Link>
          </div>
        </section>
      </ImmersiveLayout>
    );
  }

  // -------- Main checkout layout --------
  return (
    <ImmersiveLayout>
      <section className="px-6 lg:px-16 pt-24 pb-32" data-testid="checkout-page">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-seikat-pink" />
              <span className="eyebrow-immersive">{id ? 'Langkah Terakhir' : 'Final Step'}</span>
            </div>
            <h1 className="mt-4 font-heading text-5xl lg:text-6xl text-seikat-bone leading-[0.95]">
              {id ? <>Mari kami <em>antarkan</em> bunganya.</> : <>Let us <em>deliver</em> the bloom.</>}
            </h1>
          </motion.div>

          <div className="grid lg:grid-cols-[1fr_420px] gap-10 mt-14">
            {/* Left: form */}
            <div className="space-y-8">
              {/* Delivery */}
              <Card title={id ? 'Alamat Pengiriman' : 'Delivery Details'} icon={MapPin} step="01">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={id ? 'Nama Lengkap' : 'Full Name'} icon={User} required>
                    <input
                      type="text"
                      value={form.delivery_name}
                      onChange={(e) => setForm({ ...form, delivery_name: e.target.value })}
                      className="immersive-input"
                      data-testid="delivery-name"
                    />
                  </Field>
                  <Field label={id ? 'Telepon' : 'Phone'} icon={Phone} required>
                    <input
                      type="tel"
                      value={form.delivery_phone}
                      onChange={(e) => setForm({ ...form, delivery_phone: e.target.value })}
                      className="immersive-input"
                      placeholder="+62..."
                      data-testid="delivery-phone"
                    />
                  </Field>
                </div>
                <Field label={id ? 'Alamat Lengkap' : 'Full Address'} icon={Mail} required>
                  <textarea
                    value={form.delivery_address}
                    onChange={(e) => setForm({ ...form, delivery_address: e.target.value })}
                    className="immersive-input resize-none"
                    rows={3}
                    data-testid="delivery-address"
                  />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={id ? 'Tanggal Pengiriman' : 'Delivery Date'} icon={Calendar}>
                    <input
                      type="date"
                      value={form.delivery_date}
                      onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
                      className="immersive-input"
                      data-testid="delivery-date"
                    />
                  </Field>
                  <Field label={id ? 'Catatan' : 'Notes'} icon={FileText}>
                    <input
                      type="text"
                      value={form.delivery_notes}
                      onChange={(e) => setForm({ ...form, delivery_notes: e.target.value })}
                      className="immersive-input"
                      data-testid="delivery-notes"
                    />
                  </Field>
                </div>

                {/* Delivery zones */}
                {deliveryZones.length > 0 && (
                  <div className="mt-2">
                    <p className="font-body text-xs tracking-widest uppercase text-seikat-bone-muted mb-3">
                      {id ? 'Pilih Zona Pengiriman' : 'Select Delivery Zone'}
                    </p>
                    <div className="grid sm:grid-cols-2 gap-2" data-testid="delivery-zone-selector">
                      {deliveryZones.map((zone) => {
                        const active = selectedZone?.zone_id === zone.zone_id;
                        return (
                          <button
                            key={zone.zone_id}
                            onClick={() => setSelectedZone(zone)}
                            data-testid={`zone-${zone.zone_id}`}
                            data-magnet
                            className={`p-3 rounded-xl border text-left transition-all ${
                              active
                                ? 'border-seikat-pink bg-seikat-pink/10'
                                : 'border-white/10 hover:border-white/25 bg-white/[0.03]'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-body text-sm text-seikat-bone">{id ? zone.name_id : zone.name_en}</span>
                              <span className="font-body text-xs text-seikat-pink">Rp {fmt(zone.fee)}</span>
                            </div>
                            <p className="font-body text-[10px] text-seikat-bone-muted mt-0.5">
                              {id ? zone.estimated_time_id : zone.estimated_time_en}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>

              {/* Payment */}
              <Card title={id ? 'Pembayaran' : 'Payment'} icon={Lock} step="02">
                <div className="space-y-3">
                  <div className="p-4 rounded-xl border border-seikat-pink/40 bg-seikat-pink/10 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-seikat-pink/20 flex items-center justify-center">
                      <CreditCard size={16} className="text-seikat-pink" />
                    </div>
                    <div className="flex-1">
                      <p className="font-body text-sm text-seikat-bone">{id ? 'Transfer Bank' : 'Bank Transfer'}</p>
                      <p className="font-body text-xs text-seikat-bone/55">{bankConfig?.bank_name} · {bankConfig?.account_number}</p>
                    </div>
                    <span className="text-[10px] tracking-widest uppercase text-seikat-lime">{id ? 'Aktif' : 'Active'}</span>
                  </div>
                  <div className="p-4 rounded-xl border border-white/8 bg-white/[0.02] flex items-center gap-3 opacity-60" data-testid="payment-midtrans-disabled">
                    <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                      <CreditCard size={16} className="text-seikat-bone-muted" />
                    </div>
                    <div className="flex-1">
                      <p className="font-body text-sm text-seikat-bone">{id ? 'Pembayaran Online' : 'Online Payment'}</p>
                      <p className="font-body text-xs text-seikat-bone/45">Midtrans · {id ? 'akan datang' : 'coming soon'}</p>
                    </div>
                    <span className="text-[10px] tracking-widest uppercase text-seikat-bone-muted">{id ? 'Segera' : 'Soon'}</span>
                  </div>
                </div>
              </Card>

              {/* Add-ons */}
              <Card title={id ? 'Tambahan' : 'Add-ons'} icon={Gift} step="03">
                <div className="space-y-3">
                  <AddonToggle
                    active={addMemoryPage}
                    onToggle={() => setAddMemoryPage((v) => !v)}
                    icon={QrCode}
                    title={id ? 'Memory Page + QR' : 'Memory Page + QR'}
                    desc={id ? 'Halaman memori digital yang bisa dipindai.' : 'A scannable digital memory page.'}
                    price={`+Rp ${fmt(memoryPagePrice)}`}
                    testid="addon-memory-page"
                  />
                  <AddonToggle
                    active={addGiftCard}
                    onToggle={() => setAddGiftCard((v) => !v)}
                    icon={Gift}
                    title={id ? 'Kartu Hadiah' : 'Gift Card'}
                    desc={id ? 'Pesan personal yang dirangkai.' : 'A personal handwritten note.'}
                    price={`+Rp ${fmt(giftCardPrice)}`}
                    testid="addon-gift-card"
                  />
                  {addGiftCard && (
                    <Link to="/gift-card" className="block">
                      <div className="flex items-center justify-center gap-2 p-3 rounded-xl border border-dashed border-seikat-pink/40 text-xs tracking-widest uppercase text-seikat-pink hover:bg-seikat-pink/5 transition-all">
                        <Plus size={14} /> {id ? 'Desain Kartu Hadiah' : 'Design Gift Card'}
                      </div>
                    </Link>
                  )}
                </div>
              </Card>

              {/* Promo */}
              <Card title={id ? 'Kode Promo' : 'Promo Code'} icon={Ticket} step="04">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    placeholder={id ? 'Masukkan kode promo' : 'Enter code'}
                    className="immersive-input flex-1 uppercase tracking-widest"
                    data-testid="promo-code-input"
                  />
                  <button
                    onClick={handleValidatePromo}
                    disabled={promoLoading || !promoCode.trim()}
                    className="btn-pill px-6 disabled:opacity-40"
                    data-testid="apply-promo-btn"
                  >
                    {promoLoading ? <Loader2 size={14} className="animate-spin" /> : (id ? 'Terapkan' : 'Apply')}
                  </button>
                </div>
                {promoResult && (
                  <div className="mt-3 flex items-center justify-between p-3 rounded-xl bg-seikat-lime/10 border border-seikat-lime/25" data-testid="promo-applied">
                    <span className="font-body text-xs text-seikat-lime">
                      {promoResult.code} · {promoResult.discount_type === 'percentage' ? `${promoResult.discount_value}%` : `Rp ${fmt(promoResult.discount_value)}`}
                    </span>
                    <button onClick={() => { setPromoResult(null); setPromoCode(''); }} className="text-xs text-seikat-bone-muted hover:text-seikat-pink transition-colors">
                      {id ? 'Hapus' : 'Remove'}
                    </button>
                  </div>
                )}
              </Card>

              {/* Loyalty Points */}
              {loyaltyBalance > 0 && (
                <Card title={id ? 'Poin Loyalitas' : 'Loyalty Points'} icon={Coins} step="05">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-xl bg-seikat-pink/10 border border-seikat-pink/25">
                      <div>
                        <p className="font-body text-xs text-seikat-bone-muted mb-1">{id ? 'Saldo Tersedia' : 'Available Balance'}</p>
                        <p className="font-heading text-2xl text-seikat-bone">{fmt(loyaltyBalance)} {id ? 'poin' : 'points'}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-seikat-pink/20 flex items-center justify-center">
                        <Coins size={20} className="text-seikat-pink" />
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <input
                        type="number"
                        min="0"
                        max={Math.min(loyaltyBalance, total)}
                        value={redeemPoints}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          const maxAllowed = Math.min(loyaltyBalance, total);
                          setRedeemPoints(Math.max(0, Math.min(val, maxAllowed)));
                        }}
                        placeholder={id ? 'Jumlah poin' : 'Points amount'}
                        className="immersive-input flex-1"
                        data-testid="loyalty-points-input"
                      />
                      <button
                        onClick={() => setRedeemPoints(Math.min(loyaltyBalance, total))}
                        className="btn-pill px-6"
                        data-testid="use-max-points-btn"
                      >
                        {id ? 'Maksimal' : 'Max'}
                      </button>
                    </div>

                    {redeemPoints > 0 && (
                      <div className="p-3 rounded-xl bg-seikat-lime/10 border border-seikat-lime/25" data-testid="loyalty-applied">
                        <p className="font-body text-xs text-seikat-lime">
                          {id ? `${fmt(redeemPoints)} poin = -Rp ${fmt(redeemPoints)}` : `${fmt(redeemPoints)} points = -Rp ${fmt(redeemPoints)}`}
                        </p>
                        <p className="font-body text-[10px] text-seikat-bone-muted mt-1">
                          {id ? '1 poin = Rp 1' : '1 point = Rp 1'}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </div>

            {/* Right: summary */}
            <aside className="lg:sticky lg:top-10 h-fit">
              <div className="glass-dark rounded-2xl p-7">
                <p className="eyebrow-immersive text-seikat-lime">{id ? 'Ringkasan' : 'Summary'}</p>
                <h3 className="font-heading text-3xl text-seikat-bone mt-1 mb-6">{id ? 'Rangkaianmu' : 'Your Weave'}</h3>

                <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {cart.items.map((item) => (
                    <div key={item.item_id} className="flex justify-between text-sm font-body">
                      <span className="text-seikat-bone/70 line-clamp-1 pr-2">
                        {(id ? item.product?.name_id : item.product?.name_en) || 'Bouquet'} <span className="text-seikat-bone-muted">x{item.quantity || 1}</span>
                      </span>
                      <span className="text-seikat-bone whitespace-nowrap">Rp {fmt((item.product?.price || 0) * (item.quantity || 1))}</span>
                    </div>
                  ))}
                </div>

                <div className="my-5 h-px bg-white/8" />

                <div className="space-y-2.5 font-body text-sm">
                  <Row label={id ? 'Subtotal' : 'Subtotal'} value={`Rp ${fmt(subtotal)}`} />
                  {addMemoryPage && <Row label={id ? 'Memory Page' : 'Memory Page'} value={`Rp ${fmt(memoryPagePrice)}`} accent="lime" />}
                  {addGiftCard && <Row label={id ? 'Kartu Hadiah' : 'Gift Card'} value={`Rp ${fmt(giftCardPrice)}`} accent="lime" />}
                  {promoResult && <Row label={id ? 'Diskon Promo' : 'Promo Discount'} value={`-Rp ${fmt(promoDiscount)}`} accent="lime" />}
                  {redeemPoints > 0 && <Row label={id ? 'Diskon Loyalitas' : 'Loyalty Discount'} value={`-Rp ${fmt(loyaltyDiscount)}`} accent="pink" />}
                  <Row
                    label={
                      <span>
                        {id ? 'Ongkir' : 'Delivery'}
                        {selectedZone && (
                          <span className="text-seikat-bone-muted text-[10px] ml-1">({id ? selectedZone.name_id : selectedZone.name_en})</span>
                        )}
                      </span>
                    }
                    value={`Rp ${fmt(deliveryFee)}`}
                  />
                </div>

                <div className="my-5 h-px bg-white/8" />

                <div className="flex items-baseline justify-between mb-6">
                  <span className="eyebrow-immersive">{id ? 'Total' : 'Total'}</span>
                  <span className="font-heading text-3xl text-seikat-bone">Rp {fmt(total)}</span>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="btn-pill btn-pill--solid w-full justify-center disabled:opacity-50"
                  data-testid="place-order-btn"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                  {id ? 'Pesan Sekarang' : 'Place Order'}
                </button>
                <p className="font-body text-[10px] text-seikat-bone-muted text-center mt-3">
                  {id ? 'Transaksi aman · Dirangkai dengan hati.' : 'Secure checkout · Woven with care.'}
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
}

// ---------- Sub-components ----------
function Card({ title, icon: Icon, step, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: EASE }}
      className="glass-dark rounded-2xl p-7"
    >
      <header className="flex items-center gap-4 mb-6">
        {step && <span className="font-heading text-3xl text-seikat-pink/60 leading-none">{step}</span>}
        <div className="flex-1">
          <h2 className="font-heading text-2xl text-seikat-bone">{title}</h2>
        </div>
        {Icon && <Icon size={16} className="text-seikat-bone-muted" strokeWidth={1.4} />}
      </header>
      <div className="space-y-4">{children}</div>
    </motion.div>
  );
}

function Field({ label, icon: Icon, children, required }) {
  return (
    <label className="block">
      <div className="flex items-center gap-1.5 mb-2 font-body text-[10px] tracking-[0.25em] uppercase text-seikat-bone-muted">
        {Icon && <Icon size={11} strokeWidth={1.6} />}
        <span>{label}{required && <span className="text-seikat-pink ml-1">*</span>}</span>
      </div>
      {children}
    </label>
  );
}

function AddonToggle({ active, onToggle, icon: Icon, title, desc, price, testid }) {
  return (
    <button
      onClick={onToggle}
      data-testid={testid}
      data-magnet
      className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
        active
          ? 'border-seikat-lime/50 bg-seikat-lime/10'
          : 'border-white/10 hover:border-white/20 bg-white/[0.03]'
      }`}
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${active ? 'bg-seikat-lime/20' : 'bg-white/5'}`}>
        <Icon size={18} className={active ? 'text-seikat-lime' : 'text-seikat-bone-muted'} strokeWidth={1.5} />
      </div>
      <div className="flex-1">
        <p className="font-body text-sm text-seikat-bone">{title}</p>
        <p className="font-body text-[11px] text-seikat-bone/55">{desc}</p>
      </div>
      <span className={`font-body text-sm ${active ? 'text-seikat-lime' : 'text-seikat-pink'}`}>{price}</span>
    </button>
  );
}

function Row({ label, value, accent }) {
  const colorClass = accent === 'lime' ? 'text-seikat-lime' : accent === 'pink' ? 'text-seikat-pink' : 'text-seikat-bone';
  return (
    <div className="flex justify-between">
      <span className="text-seikat-bone/65">{label}</span>
      <span className={colorClass}>{value}</span>
    </div>
  );
}
